/**
 * Pending organization invitation helpers.
 *
 * An invitee following an `/invite/:token` link is, by definition, usually logged
 * out and has often never signed up. The route used to sit behind `ProtectedRoute`,
 * so those users were bounced to `/login` (or `/signup`) and the token was lost.
 *
 * We now stash the token before any auth redirect, replay it once the user is
 * authenticated, and record that they joined an existing org so the signup
 * account-type funnel does not re-capture them.
 */

const PENDING_INVITE_TOKEN_KEY = 'pending_org_invite_token'
const ORG_INVITE_ACCEPTED_KEY = 'org_invite_accepted'

const safeGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

const safeSet = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Storage unavailable (private mode / disabled) - nothing we can do.
  }
}

const safeRemove = (key: string): void => {
  try {
    localStorage.removeItem(key)
  } catch {
    // Storage unavailable - nothing we can do.
  }
}

/** Remember the invitation token across an auth redirect. */
export const setPendingInviteToken = (token: string): void => {
  if (!token) return
  safeSet(PENDING_INVITE_TOKEN_KEY, token)
}

/** The invitation token the user still needs to accept, if any. */
export const getPendingInviteToken = (): string | null => {
  const token = safeGet(PENDING_INVITE_TOKEN_KEY)
  return token && token.trim() ? token : null
}

/** Drop the stashed token once the invitation reaches a terminal state. */
export const clearPendingInviteToken = (): void => {
  safeRemove(PENDING_INVITE_TOKEN_KEY)
}

/** The route that resumes a stashed invitation, or null when there is none. */
export const getPendingInviteRedirect = (): string | null => {
  const token = getPendingInviteToken()
  return token ? `/invite/${encodeURIComponent(token)}` : null
}

/**
 * Record that this user joined an existing organization via an invitation.
 *
 * `ProtectedRoute` otherwise redirects to `/signup` whenever
 * `signup_account_type` is missing. An invitee never picks an account type -
 * their organization already exists - so this flag satisfies that gate without
 * pushing them into the institutional-profile funnel.
 */
export const markOrgInviteAccepted = (): void => {
  safeSet(ORG_INVITE_ACCEPTED_KEY, 'true')
}

/** Whether this user joined an organization through an invitation. */
export const hasAcceptedOrgInvite = (): boolean => safeGet(ORG_INVITE_ACCEPTED_KEY) === 'true'
